import {
  Box,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { AccountListItem } from "../../types";
import { useEffect, useState } from "react";
import { isTextEllipsed } from "../../utils/general_utils";
import { AnalysisStatus } from "../../types/companyTypes";
import { indicatorMapper, RateIndicatorIcon } from "./RateIndicatorIcon";
import { formatDateRange } from "../../utils/formatDateRange";
import { AccountPlanStatus } from "../../types/enums";
import { ReuseTooltip } from "./ReuseTooltip";

const TooltipItem = ({
  text,
  value,
  color,
}: {
  text: string;
  value: string;
  color: string;
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap="14px"
    >
      <Stack direction="row" alignItems="center" gap="6px">
        <Box
          sx={{
            width: "18px",
            height: "14px",
            backgroundColor: color,
          }}
        ></Box>
        <Typography
          variant="body2"
          align="center"
          color="white"
          fontWeight={500}
        >
          {text}
        </Typography>
      </Stack>
      <Typography variant="body2" align="center" color="white" fontWeight={500}>
        {value}
      </Typography>
    </Stack>
  );
};

const LinerProgress = ({ data }: { data: AccountListItem }) => {
  const { indicator, change, changed_at, score, your_data, external_data } =
    data.discovery;

  const userData = your_data || 0;
  const infuseData = external_data || 0;

  return (
    <Tooltip
      title={
        <Stack gap="6px" paddingY="4px">
          <TooltipItem
            value={`${userData}%`}
            text="Your data"
            color="#1C3C6C"
          />
          <TooltipItem
            value={`${infuseData}%`}
            text="INFUSE Data Extension"
            color="#FE8836"
          />
          {indicator && (
            <>
              <Stack
                flexDirection="row"
                gap="20px"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="white">
                  Last update:
                </Typography>
                <Typography variant="body2" color="white" fontWeight={500}>
                  {formatDateRange(changed_at ?? "", true)}
                </Typography>
              </Stack>
              <Stack
                flexDirection="row"
                gap="4px"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="white">
                  Change:
                </Typography>
                <Typography variant="body2" color="white" fontWeight={500}>
                  {change && change > 0 ? `+${change}` : change}%
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      }
      placement="top"
      arrow
      sx={{
        fontSize: "12px",
        lineHeight: "16px",
        fontWeight: 400,
        "&:hover": {
          visibility: "visible",
          opacity: 1,
        },
      }}
    >
      <Stack
        alignItems="center"
        onMouseEnter={(event: React.MouseEvent<HTMLDivElement>) => {
          const target = event.currentTarget;
          isTextEllipsed(target);
        }}
      >
        <Stack alignItems="center">
          <Stack
            sx={{ position: "relative", width: "42px", height: "42px" }}
            justifyContent="center"
          >
            <Stack
              sx={{
                position: "absolute",
                left: -26,
                top: 10,
                zIndex: 2,
              }}
            >
              <RateIndicatorIcon
                indicatorRate={indicator}
                showTooltip={false}
              />
            </Stack>
            <CircularProgress
              size={42}
              thickness={4}
              variant="determinate"
              sx={{
                color: "#D4D4D4",
                position: "absolute",
                left: 0,
                top: 0,
                zIndex: 1,
                opacity: 1,
              }}
              value={100}
            />
            <CircularProgress
              size={42}
              thickness={4}
              variant="determinate"
              sx={{
                color: "#1C3C6C",
                position: "absolute",
                left: 0,
                top: 0,
                zIndex: 3,
                opacity: 1,
              }}
              value={userData}
            />
            <CircularProgress
              size={42}
              thickness={4}
              variant="determinate"
              sx={{
                color: "#FE8836",
                position: "absolute",
                left: 0,
                top: 0,
                zIndex: 2,
              }}
              value={userData + infuseData}
            />
            <Typography
              variant="body1"
              align="center"
              sx={{
                flexShrink: 1,
                fontSize: 10,
                fontWeight: 600,
                ...(data.penetration_score_indicator && {
                  color:
                    indicatorMapper[data.penetration_score_indicator].color,
                }),
              }}
            >
              {score || 0}%
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Tooltip>
  );
};

const AnalyseProgress = ({
  setIsAnalyse,
  progress,
  isAnalyzing,
  isScrapingQueued,
  isAnalyzeQueued,
}: {
  setIsAnalyse: (isAnalyse: boolean) => void;
  progress: number;
  isAnalyzing: boolean;
  isScrapingQueued: boolean;
  isAnalyzeQueued: boolean;
}) => {
  useEffect(() => {
    if (progress === 100) {
      setIsAnalyse(false);
    }
  }, [progress]);

  return (
    <Stack
      direction="row"
      gap="8px"
      alignItems="center"
      justifyContent="center"
    >
      {!isAnalyzing ? (
        <>
          <CircularProgress size={24} sx={{ color: "custom.subTitleModal" }} />
          <Typography variant="button" sx={{ fontWeight: 500 }}>
            {isScrapingQueued
              ? "Queued"
              : isAnalyzeQueued
                ? "Queued"
                : "Searching"}
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress
            size={24}
            sx={{ color: "custom.subTitleModal", flexShrink: 0 }}
          />
          <Typography
            variant="button"
            sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
          >
            Analyzing: {`${progress}%`}
          </Typography>
        </>
      )}
    </Stack>
  );
};

export const ProgressBar = ({ data }: { data: AccountListItem }) => {
  const [isAnalyse, setIsAnalyse] = useState<boolean>(false);
  const {
    status,
    scraping_status: currentScrapingStatus,
    scraping_update_status: currentScrapingUpdateStatus,
    progress,
    account_plan_status,
  } = data;

  useEffect(() => {
    if (progress === 100) {
      setIsAnalyse(false);
    }
  }, [progress]);

  const isScraping = [AnalysisStatus.queued, AnalysisStatus.in_progress].some(
    (status) => status === currentScrapingStatus,
  );

  const isScrapingUpdate = [
    AnalysisStatus.queued,
    AnalysisStatus.in_progress,
  ].some((status) => status === currentScrapingUpdateStatus);

  const isAnalyzing = AnalysisStatus.in_progress === status;
  const isAnalyzeQueued = AnalysisStatus.queued === status;
  const isScrapingQueued = [
    currentScrapingStatus,
    currentScrapingUpdateStatus,
  ].some((status) => status === AnalysisStatus.queued);

  useEffect(() => {
    if (
      isScraping ||
      isScrapingUpdate ||
      isAnalyzeQueued ||
      isAnalyzing ||
      isScrapingQueued
    ) {
      setIsAnalyse(true);
    } else {
      setIsAnalyse(false);
    }
  }, [
    isScraping,
    isScrapingUpdate,
    isAnalyzing,
    isAnalyzeQueued,
    isScrapingQueued,
  ]);

  if (account_plan_status === AccountPlanStatus.in_progress) {
    return (
      <Stack
        direction="row"
        gap="8px"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={24} sx={{ color: "custom.subTitleModal" }} />
        <ReuseTooltip
          text={
            "Generation may take a few hours to complete due to the high volume of information to be analyzed." +
            " You'll be notified by email when the generation is complete."
          }
          maxWidth="184px"
        >
          <Typography
            variant="button"
            sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
          >
            Generating account plan
          </Typography>
        </ReuseTooltip>
      </Stack>
    );
  }

  return isAnalyse ? (
    <AnalyseProgress
      isAnalyzing={isAnalyzing}
      isScrapingQueued={isScrapingQueued}
      isAnalyzeQueued={isAnalyzeQueued}
      progress={progress}
      setIsAnalyse={setIsAnalyse}
    />
  ) : (
    <LinerProgress data={data} />
  );
};
